import React, { useState, useEffect } from 'react';
import MaskedInput from 'react-text-mask';
import { toast } from 'react-toastify';
import cep from 'cep-promise';
import { LoadingDots, ProcessButton, ProcessHeader } from '../../../../components';
import DeliveryValues from './DeliveryValues';
import './styles.css';

function useNewAddress(commands) {
  const [searchingCep, setSearchingCep] = useState(false);

  const fetchCEP = (CEP, deliveryAddr) => {
    setSearchingCep(true);
    cep(CEP).then((data) => {
      if (data) {
        commands.order.changeDataAttribute('deliveryAddr', {
          ...deliveryAddr,
          zipCode: data.cep,
          street: data.street,
          neighborhood: data.neighborhood,
          city: data.city,
          uf: data.state,
        });
        commands.process.updateDataController('newAddressFetchByCep', {
          zipCode: !!data.cep,
          street: !!data.street,
          neighborhood: !!data.neighborhood,
          city: !!data.city,
          uf: !!data.state,
        });
      } else {
        toast('CEP não encontrado', { type: toast.TYPE.WARNING });
        commands.order.changeDataAttribute('deliveryAddr', { zipCode: CEP });
      }
      setSearchingCep(false);
    }).catch((err) => {
      toast('CEP não encontrado', { type: toast.TYPE.WARNING });
      commands.order.changeDataAttribute('deliveryAddr', { zipCode: CEP });
      setSearchingCep(false);
    });
  };

  const cleanCepInput = (deliveryAddr) => (e) => {
    const cleaned = (`${e.target.value}`).replace(/\D/g, '');
    commands.process.updateDataController('newAddressFetchByCep', {});
    commands.order.changeDataAttribute('deliveryAddr', { zipCode: cleaned });
    commands.delivery.resetOneAddressValues();
    if (cleaned.length >= 8) fetchCEP(cleaned, deliveryAddr);
  };

  const cleanNumberInput = (deliveryAddr) => (e) => {
    const cleaned = (`${e.target.value}`).replace(/\D/g, '');
    commands.order.changeDataAttribute('deliveryAddr', {
      ...deliveryAddr,
      number: cleaned,
    });
    commands.delivery.resetOneAddressValues();
  };

  const getDeliveryValues = (deliveryAddr) => commands.delivery.getOneAddressDeliveryValues(deliveryAddr);

  const changeAddressAttribute = (value, attribute, deliveryAddr) => commands.order.changeDataAttribute('deliveryAddr', { ...deliveryAddr, [attribute]: value });

  const goBack = (controllers) => (controllers.newAddressFromAddressesList
    ? commands.process.updateMultipleDataControllers({ newAddress: false, newAddressFromAddressesList: false })
    : commands.process.previousStep());

  const setAddressId = (res) => commands.order.changeDataAttribute('deliveryAddrId', res.addressesIds[0]);

  const registerAddress = async (deliveryAddr, deliveryValues) => {
    if (!deliveryAddr.zipCode || !deliveryAddr.street || !deliveryAddr.number
      || !deliveryAddr.neighborhood || !deliveryAddr.city || !deliveryAddr.uf) { return toast('É necessário preencher os campos faltantes de endereço.', { type: toast.TYPE.WARNING }); }
    if (!deliveryValues) return;
    await commands.addresses.createAddresses([deliveryAddr], setAddressId);
  };

  const goToNextStep = () => {
    commands.process.updateDataController('newAddress', false);
    commands.process.goToStep(3);
  };

  return ({
    localStates: {
      searchingCep,
    },
    eventHandlers: {
      cleanCepInput,
      cleanNumberInput,
      getDeliveryValues,
      changeAddressAttribute,
      goBack,
      registerAddress,
      goToNextStep,
    },
  });
}

export default function NewAddress(props) {
  const { commands, sharedStates, metaStates } = props;
  const {
    localStates: {
      searchingCep,
    }, eventHandlers,
  } = useNewAddress(commands);
  const fetchByCep = sharedStates?.process?.controllers?.newAddressFetchByCep ?? {};
  
  useEffect(() => {
    if (metaStates.address.isCreated) eventHandlers.goToNextStep();
  }, [metaStates.address.isCreated]);

  return (
    <section className="step-container">
      <ProcessHeader goBackAction={() => eventHandlers.goBack(sharedStates.process.controllers)} rightText={sharedStates.headerRightText} />
      <h1 className="step-title">
        Qual é o endereço de entrega?
      </h1>

      <div className="step-input-group">
        <div className="floating-label-input">
          {searchingCep
            ? (
              <div className="searching-cep">
                <p>Buscando  </p>
                <LoadingDots />
              </div>
            )
            : (
              <MaskedInput
                placeholder="CEP"
                id="zipCode"
                type="text"
                name="zipCode"
                showMask={searchingCep}
                value={sharedStates?.order?.deliveryAddr?.zipCode || ''}
                onChange={eventHandlers.cleanCepInput(sharedStates?.order?.deliveryAddr)}
                mask={[/[0-9]/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/]}
                onBlur={eventHandlers.getDeliveryValues}
              />
            )}
          <label htmlFor="zipCode">CEP</label>
        </div>

        <div className="floating-label-input">
          <input disabled={searchingCep || fetchByCep.street} placeholder="Rua" id="street" type="text" name="street" value={sharedStates?.order?.deliveryAddr?.street || ''} onChange={(e) => eventHandlers.changeAddressAttribute(e.target.value, 'street', sharedStates?.order?.deliveryAddr)} />
          <label htmlFor="street">Rua</label>
        </div>

        <div className="floating-label-input">
          <input disabled={searchingCep} placeholder="Número" id="number" type="text" name="number" value={sharedStates?.order?.deliveryAddr?.number || ''} onChange={eventHandlers.cleanNumberInput(sharedStates?.order?.deliveryAddr)} onBlur={() => eventHandlers.getDeliveryValues(sharedStates?.order?.deliveryAddr)} />
          <label htmlFor="number">Número</label>
        </div>

        <div className="floating-label-input">
          <input disabled={searchingCep} placeholder="Complemento" id="complement" type="text" name="complement" value={sharedStates?.order?.deliveryAddr?.complement || ''} onChange={(e) => eventHandlers.changeAddressAttribute(e.target.value, 'complement', sharedStates?.order?.deliveryAddr)} />
          <label htmlFor="complement">Complemento</label>
        </div>

        <div className="floating-label-input">
          <input disabled={searchingCep || (fetchByCep.neighborhood)} placeholder="Bairro" id="neighborhood" type="text" name="neighborhood" value={sharedStates?.order?.deliveryAddr?.neighborhood || ''} onChange={(e) => eventHandlers.changeAddressAttribute(e.target.value, 'neighborhood', sharedStates?.order?.deliveryAddr)} />
          <label htmlFor="neighborhood">Bairro</label>
        </div>

        <div className="floating-label-input">
          <input disabled={searchingCep || (fetchByCep.city)} placeholder="Cidade" id="city" type="text" name="city" value={sharedStates?.order?.deliveryAddr?.city || ''} onChange={(e) => eventHandlers.changeAddressAttribute(e.target.value, 'city', sharedStates?.order?.deliveryAddr)} />
          <label htmlFor="city">Cidade</label>
        </div>

        <div className="floating-label-input">
          <input disabled={searchingCep || (fetchByCep.uf)} placeholder="Estado" id="uf" type="text" name="uf" maxLength="2" value={sharedStates?.order?.deliveryAddr?.uf || ''} onChange={(e) => eventHandlers.changeAddressAttribute(e.target.value.toUpperCase(), 'uf', sharedStates?.order?.deliveryAddr)} />
          <label htmlFor="uf">Estado</label>
        </div>
      </div>
      { !metaStates?.delivery?.calculating && sharedStates?.delivery?.oneAddressValues && <DeliveryValues values={sharedStates?.delivery?.oneAddressValues} gratuity={sharedStates?.gratuity} freeFreight={sharedStates?.order?.freeFreight} /> }
      <ProcessButton 
        action={() => eventHandlers.registerAddress(sharedStates?.order?.deliveryAddr, sharedStates?.delivery?.oneAddressValues)} 
        text={metaStates?.delivery?.calculating ? 'Calculando distância e frete...' : null}
        actionFinished={metaStates.address.isCreated || metaStates.address.isError}
        />
    </section>
  );
}
